import { useState, useRef, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { getVendors, createVendor, Vendor } from '@/features/procurement/services/procurementApi';
import {
    Search, ChevronDown, Check, Plus, X, Building2,
    Phone, Mail, User, Star, Loader2, Sparkles, CheckCircle
} from 'lucide-react';
import { clsx } from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';

interface SupplierComboboxProps {
    supplierId?: string;
    supplierName?: string;
    supplierContact?: string;
    supplierPhone?: string;
    supplierEmail?: string;
    onChange: (supplier: {
        supplierId: string;
        supplierName: string;
        supplierContact: string;
        supplierPhone: string;
        supplierEmail: string;
    }) => void;
    error?: string;
    disabled?: boolean;
}

export function SupplierCombobox({
    supplierId = '',
    supplierName = '',
    supplierContact = '',
    supplierPhone = '',
    supplierEmail = '',
    onChange,
    error,
    disabled = false,
}: SupplierComboboxProps) {
    const user = useAuthStore((s) => s.user);
    const tenantId = user?.tenantId;
    const addToast = useToastStore((s) => s.addToast);
    const queryClient = useQueryClient();

    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showQuickCreateModal, setShowQuickCreateModal] = useState(false);

    // Quick create form state
    const [newVendorName, setNewVendorName] = useState('');
    const [newVendorContact, setNewVendorContact] = useState('');
    const [newVendorPhone, setNewVendorPhone] = useState('');
    const [newVendorEmail, setNewVendorEmail] = useState('');

    const containerRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Fetch vendors from procurement API
    const { data: vendors = [], isLoading } = useQuery({
        queryKey: ['vendors', tenantId],
        queryFn: () => (tenantId ? getVendors(tenantId, { limit: 100 }) : Promise.resolve([])),
        enabled: !!tenantId,
        staleTime: 60_000,
    });

    // Create Vendor Mutation
    const createVendorMutation = useMutation({
        mutationFn: (newVendor: Partial<Vendor>) => createVendor(tenantId!, newVendor),
        onSuccess: (created) => {
            queryClient.invalidateQueries({ queryKey: ['vendors', tenantId] });
            onChange({
                supplierId: created.id,
                supplierName: created.name,
                supplierContact: created.contactPerson || '',
                supplierPhone: created.phone || '',
                supplierEmail: created.email || '',
            });
            setShowQuickCreateModal(false);
            setIsOpen(false);
            addToast({
                type: 'success',
                title: 'Supplier Registered',
                message: `"${created.name}" is now saved and linked to this product.`,
            });
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'Could not create supplier',
                message: err?.response?.data?.message || err?.message || 'Failed to create vendor',
            });
        },
    });

    // Filter vendors
    const filteredVendors = useMemo(() => {
        if (!searchTerm.trim()) return vendors;
        const term = searchTerm.toLowerCase().trim();
        return vendors.filter((v) => {
            const nameMatch = v.name.toLowerCase().includes(term);
            const contactMatch = v.contactPerson?.toLowerCase().includes(term);
            const phoneMatch = v.phone?.toLowerCase().includes(term);
            const codeMatch = (v as any).code?.toLowerCase().includes(term) || v.accountNumber?.toLowerCase().includes(term);
            return nameMatch || contactMatch || phoneMatch || codeMatch;
        });
    }, [vendors, searchTerm]);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Focus input on open
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        } else if (!isOpen) {
            setSearchTerm('');
        }
    }, [isOpen]);

    const handleSelectVendor = (v: Vendor) => {
        onChange({
            supplierId: v.id,
            supplierName: v.name,
            supplierContact: v.contactPerson || '',
            supplierPhone: v.phone || '',
            supplierEmail: v.email || '',
        });
        setIsOpen(false);
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange({
            supplierId: '',
            supplierName: '',
            supplierContact: '',
            supplierPhone: '',
            supplierEmail: '',
        });
    };

    const handleUseCustomName = (name: string) => {
        onChange({
            supplierId: '',
            supplierName: name.trim(),
            supplierContact: supplierContact,
            supplierPhone: supplierPhone,
            supplierEmail: supplierEmail,
        });
        setIsOpen(false);
    };

    const handleQuickCreateSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!newVendorName.trim()) return;
        createVendorMutation.mutate({
            name: newVendorName.trim(),
            contactPerson: newVendorContact.trim() || undefined,
            phone: newVendorPhone.trim() || undefined,
            email: newVendorEmail.trim() || undefined,
            isActive: true,
        });
    };

    return (
        <div ref={containerRef} className="relative">
            {/* Combobox Trigger */}
            <div
                role="button"
                tabIndex={0}
                onClick={() => !disabled && setIsOpen(!isOpen)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        if (!disabled) setIsOpen(!isOpen);
                    }
                }}
                className={clsx(
                    'w-full px-4 py-3 border-2 rounded-xl text-sm flex items-center justify-between cursor-pointer transition-colors',
                    error
                        ? 'border-rose-300 bg-rose-50/50 dark:bg-rose-950/20'
                        : isOpen
                        ? 'border-emerald-500 ring-2 ring-emerald-500/20 bg-white dark:bg-slate-900'
                        : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-600',
                    disabled && 'opacity-60 cursor-not-allowed bg-slate-100 dark:bg-slate-800'
                )}
            >
                <div className="flex items-center gap-2 truncate">
                    {supplierName ? (
                        <>
                            <Building2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                            <span className="font-semibold text-slate-800 dark:text-slate-200 truncate">
                                {supplierName}
                            </span>
                            {supplierId ? (
                                <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-emerald-100/70 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">
                                    Linked Vendor
                                </span>
                            ) : (
                                <span className="text-[10px] uppercase font-medium px-1.5 py-0.5 rounded bg-amber-100/70 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">
                                    Custom Name
                                </span>
                            )}
                        </>
                    ) : (
                        <span className="text-slate-400 dark:text-slate-500 flex items-center gap-1.5">
                            <Building2 className="w-4 h-4 text-slate-400" />
                            Search or select vendor / supplier...
                        </span>
                    )}
                </div>

                <div className="flex items-center gap-1">
                    {supplierName && !disabled && (
                        <button
                            type="button"
                            onClick={handleClear}
                            className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                            title="Clear supplier"
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                    )}
                    <ChevronDown className={clsx('w-4 h-4 text-slate-400 transition-transform duration-200', isOpen && 'rotate-180')} />
                </div>
            </div>

            {/* Dropdown Menu */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.15 }}
                        className="absolute z-50 w-full mt-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl overflow-hidden"
                    >
                        {/* Search Bar */}
                        <div className="p-2 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/50">
                            <div className="relative">
                                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search by vendor name, code, contact or phone..."
                                    className="w-full pl-9 pr-3 py-2 text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 dark:text-slate-200"
                                />
                                {searchTerm && (
                                    <button
                                        type="button"
                                        onClick={() => setSearchTerm('')}
                                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Vendors List */}
                        <div className="max-h-60 overflow-y-auto divide-y divide-slate-50 dark:divide-slate-800">
                            {isLoading ? (
                                <div className="p-4 flex items-center justify-center gap-2 text-xs text-slate-400">
                                    <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                                    Loading vendors...
                                </div>
                            ) : filteredVendors.length === 0 ? (
                                <div className="p-4 text-center">
                                    <p className="text-xs text-slate-400 dark:text-slate-500 mb-2">
                                        No registered vendors matching "{searchTerm}"
                                    </p>
                                    {searchTerm && (
                                        <button
                                            type="button"
                                            onClick={() => handleUseCustomName(searchTerm)}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 hover:bg-emerald-100 rounded-lg font-medium transition-colors"
                                        >
                                            <Sparkles className="w-3.5 h-3.5" />
                                            Use "{searchTerm}" as custom supplier name
                                        </button>
                                    )}
                                </div>
                            ) : (
                                filteredVendors.map((v) => {
                                    const isSelected = v.id === supplierId;

                                    return (
                                        <button
                                            key={v.id}
                                            type="button"
                                            onClick={() => handleSelectVendor(v)}
                                            className={clsx(
                                                'w-full text-left px-4 py-2.5 text-xs flex items-center justify-between transition-colors',
                                                isSelected
                                                    ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 font-semibold'
                                                    : 'hover:bg-slate-50 dark:hover:bg-slate-800/60 text-slate-700 dark:text-slate-300'
                                            )}
                                        >
                                            <div className="space-y-0.5 truncate">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-semibold text-slate-900 dark:text-slate-100 truncate">
                                                        {v.name}
                                                    </span>
                                                    {((v as any).code || v.accountNumber) && (
                                                        <span className="text-[10px] font-mono text-slate-400 px-1 py-0.2 bg-slate-100 dark:bg-slate-800 rounded">
                                                            {(v as any).code || v.accountNumber}
                                                        </span>
                                                    )}
                                                    {v.isPreferred && (
                                                        <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                                                            <Star className="w-2.5 h-2.5 fill-amber-500" /> Preferred
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-3 text-[10px] text-slate-500 dark:text-slate-400">
                                                    {v.contactPerson && (
                                                        <span className="flex items-center gap-1">
                                                            <User className="w-3 h-3 text-slate-400" />
                                                            {v.contactPerson}
                                                        </span>
                                                    )}
                                                    {v.phone && (
                                                        <span className="flex items-center gap-1">
                                                            <Phone className="w-3 h-3 text-slate-400" />
                                                            {v.phone}
                                                        </span>
                                                    )}
                                                    {v.email && (
                                                        <span className="flex items-center gap-1">
                                                            <Mail className="w-3 h-3 text-slate-400" />
                                                            {v.email}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {isSelected && (
                                                <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                                            )}
                                        </button>
                                    );
                                })
                            )}
                        </div>

                        {/* Inline "+ Register New Supplier" Trigger */}
                        <div className="p-2 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/50 flex items-center justify-between gap-2">
                            <button
                                type="button"
                                onClick={() => {
                                    setNewVendorName(searchTerm || '');
                                    setShowQuickCreateModal(true);
                                    setIsOpen(false);
                                }}
                                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 rounded-lg transition-colors border border-emerald-200/60 dark:border-emerald-800/60"
                            >
                                <Plus className="w-3.5 h-3.5" />
                                Register New Supplier (Vendor)
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Quick Register Modal */}
            <AnimatePresence>
                {showQuickCreateModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="w-full max-w-md bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-2xl p-6"
                        >
                            <div className="flex items-center justify-between pb-4 border-b border-slate-100 dark:border-slate-800 mb-4">
                                <div className="flex items-center gap-2.5">
                                    <div className="p-2 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400">
                                        <Building2 className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-slate-900 dark:text-slate-100">
                                            Register Supplier
                                        </h3>
                                        <p className="text-xs text-slate-500">
                                            Adds vendor directly into your procurement directory
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowQuickCreateModal(false)}
                                    className="p-1 text-slate-400 hover:text-slate-600 rounded-lg"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <form onSubmit={handleQuickCreateSubmit} className="space-y-3.5">
                                <div>
                                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                                        Company / Vendor Name <span className="text-rose-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={newVendorName}
                                        onChange={(e) => setNewVendorName(e.target.value)}
                                        placeholder="e.g., Dangote Sugar Refinery Plc"
                                        className="w-full px-3 py-2 text-xs border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 dark:bg-slate-800 dark:text-slate-100"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                                            Contact Person
                                        </label>
                                        <input
                                            type="text"
                                            value={newVendorContact}
                                            onChange={(e) => setNewVendorContact(e.target.value)}
                                            placeholder="e.g., John Doe"
                                            className="w-full px-3 py-2 text-xs border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 dark:bg-slate-800 dark:text-slate-100"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                                            Phone Number
                                        </label>
                                        <input
                                            type="tel"
                                            value={newVendorPhone}
                                            onChange={(e) => setNewVendorPhone(e.target.value)}
                                            placeholder="08012345678"
                                            className="w-full px-3 py-2 text-xs border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 dark:bg-slate-800 dark:text-slate-100"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                                        Email Address
                                    </label>
                                    <input
                                        type="email"
                                        value={newVendorEmail}
                                        onChange={(e) => setNewVendorEmail(e.target.value)}
                                        placeholder="orders@vendor.com"
                                        className="w-full px-3 py-2 text-xs border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 dark:bg-slate-800 dark:text-slate-100"
                                    />
                                </div>

                                <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
                                    <button
                                        type="button"
                                        onClick={() => setShowQuickCreateModal(false)}
                                        className="px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 rounded-lg transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={createVendorMutation.isPending || !newVendorName.trim()}
                                        className="px-4 py-2 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-lg shadow-sm flex items-center gap-1.5 transition-colors"
                                    >
                                        {createVendorMutation.isPending ? (
                                            <>
                                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                Registering...
                                            </>
                                        ) : (
                                            <>
                                                <CheckCircle className="w-3.5 h-3.5" />
                                                Register & Select
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}
